// src/pages/Inventory/GoodsReceiptNoteConfirm_AddListing.jsx
// ✅ Converted from: GoodsReceiptNoteConfirm_AddListing.aspx / .aspx.cs
// ✅ Uses Node routes: /api/inventory/grn-confirm-add/*
// ✅ NO rights logic
// ✅ Uses /dropdowns like your template (supplier + store + temp projects)
// ✅ Includes mode switch via /meta (A/B)
// ✅ Includes pagination + single selection validation

import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import LayoutWrapper from "../../components/LayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { Building2, Search, Save, RefreshCcw, ChevronDown, ChevronUp } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

const GoodsReceiptNoteConfirmAddListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branchType = localStorage.getItem("branchType") || "HO";

  const screenBadge = useMemo(
    () => "INFRA SCREEN : INVENTORY - GRN CONFIRM ADD LISTING",
    []
  );

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // UI state
  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // meta/defaults + mode
  const [defaultFromDate, setDefaultFromDate] = useState("");
  const [defaultToDate, setDefaultToDate] = useState("");
  const [mode, setMode] = useState("B"); // A or B (from meta)
  const [flags, setFlags] = useState({ grnre: null, grninsre: null });

  // radio selection
  const [rad, setRad] = useState(branchType === "HO" ? "HO" : "BRANCH");
  const purType = rad === "HO" ? "H" : "B"; // ASP rad_checked

  // filters
  const [supplierId, setSupplierId] = useState("Select");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [projectId, setProjectId] = useState("Select");
  const [projectCodeId, setProjectCodeId] = useState("Select");
  const [storeId, setStoreId] = useState("Select");

  // mode-specific textbox filters
  const [grnNo, setGrnNo] = useState(""); // Mode B
  const [poNo, setPoNo] = useState("");   // Mode A

  // dropdown data
  const [suppliers, setSuppliers] = useState([]);
  const [projectsName, setProjectsName] = useState([]);
  const [projectsCode, setProjectsCode] = useState([]);
  const [stores, setStores] = useState([]);

  // list
  const [rows, setRows] = useState([]);

  // selection (exactly one)
  const [selectedKey, setSelectedKey] = useState(null);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const saveBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ` +
    (!selectedKey ? "bg-gray-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700");

  const showingFrom = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, totalCount);

  // ---------------- validations ----------------
  const mustHaveFinYearId = () => {
    const id = Number(finYearId);
    if (!id || !Number.isFinite(id)) {
      toast.warn("Please select Financial Year.");
      return false;
    }
    return true;
  };

  const validateDates = () => {
    if (fromDate && toDate && toDate < fromDate) {
      toast.warn("Please select from date less than to date");
      return false;
    }
    const todayISO = new Date().toISOString().slice(0, 10);
    if (fromDate && fromDate > todayISO) {
      toast.warn("Select from date lesser than current date");
      return false;
    }
    if (toDate && toDate > todayISO) {
      toast.warn("Select to date lesser than current date");
      return false;
    }
    return true;
  };

  const normalizeSupplierId = () => {
    if (!supplierId || supplierId === "Select") return 0;
    return Number(supplierId);
  };

  const normalizeSelectToEmpty = (v) => {
    if (!v || v === "Select") return "";
    return v;
  };

  // selection toggle (single)
  const toggleSelectSingle = (key) => {
    setSelectedKey((prev) => (prev === key ? null : key));
  };

  // ---------------- API calls ----------------
  const fetchMeta = async () => {
    if (!branchId || !branchType) {
      toast.error("Missing branchId / branchType in localStorage");
      return;
    }
    if (!mustHaveFinYearId()) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-add/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType,
        },
      });

      if (res?.data?.success) {
        setFlags(res.data.flags || { grnre: null, grninsre: null });
        setMode(res.data.mode || "B");

        setDefaultFromDate(res.data.defaultFromDate || "");
        setDefaultToDate(res.data.defaultToDate || "");
        setFromDate(res.data.defaultFromDate || "");
        setToDate(res.data.defaultToDate || "");

        setRad(branchType === "HO" ? "HO" : "BRANCH");
      } else {
        toast.error(res?.data?.message || "Failed to load meta");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta error");
    }
  };

  const fetchDropdowns = async () => {
    if (!branchId || !branchType) return;
    if (!mustHaveFinYearId()) return;

    try {
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-add/dropdowns`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType,
          purType,
        },
      });

      if (res?.data?.success) {
        setSuppliers(res.data.suppliers || []);
        setStores(res.data.stores || []);
        setProjectsName(res.data.projectsName || []);
        setProjectsCode(res.data.projectsCode || []);
      } else {
        toast.error(res?.data?.message || "Failed to load dropdowns");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Dropdown load error");
    }
  };

  const fetchList = async (p = page) => {
    if (!branchId || !branchType) {
      toast.error("Missing branchId / branchType");
      return;
    }
    if (!mustHaveFinYearId()) return;
    if (!validateDates()) return;

    try {
      setLoading(true);

      const params = {
        finYearId: Number(finYearId),
        branchId: Number(branchId),
        branchType,
        purType,
        mode, // A or B

        supplierId: normalizeSupplierId(),

        fromDate: fromDate || "",
        toDate: toDate || "",

        storeId: normalizeSelectToEmpty(storeId),
        projectId: normalizeSelectToEmpty(projectId),

        // mode-specific:
        grnNo: mode === "B" ? grnNo.trim() : "",
        poNo: mode === "A" ? poNo.trim() : "",

        projectCodeId: normalizeSelectToEmpty(projectCodeId),

        page: p,
        pageSize,
      };

      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-add/list`, { params });

      if (res?.data?.success) {
        setRows(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);
        setSelectedKey(null);
      } else {
        toast.error(res?.data?.message || "Search failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Search error");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- actions ----------------
  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleRefresh = async () => {
    setSupplierId("Select");
    setProjectId("Select");
    setProjectCodeId("Select");
    setStoreId("Select");
    setGrnNo("");
    setPoNo("");

    setFromDate(defaultFromDate || "");
    setToDate(defaultToDate || "");

    setRows([]);
    setSelectedKey(null);
    setPage(1);
    setTotalCount(0);
    setTotalPages(1);

    await fetchDropdowns();
    toast.info("Reset done");
  };

  const handleSave = async () => {
    if (!mustHaveFinYearId()) return;

    if (!selectedKey) {
      toast.warn("Please select any One");
      return;
    }

    try {
      setLoading(true);

      // Mode A: PO list (selectedKey = purchaseorderid)
      if (mode === "A") {
        const row = rows.find((x) => Number(x.fld_purchaseorderid) === Number(selectedKey));
        if (!row) {
          toast.error("Selected row not found");
          return;
        }

        sessionStorage.setItem(
          "grnConfirmModeASelection",
          JSON.stringify({
            grn_type: rad === "HO" ? "HO" : "BRANCH",
            Grnscreen: "GRN Confirmation",
            grn_purchaseorder: [row],
          })
        );

        toast.success("Saved selection");
        window.location.href = `/Inventory/GoodReceiptNoteNew_EntryGST`;
        return;
      }

      // Mode B: GRN inspection list (selectedKey = grninsid)
      const res = await axios.get(`${BASE_URL}/api/inventory/grn-confirm-add/confirm-details`, {
        params: {
          finYearId: Number(finYearId),
          grnInsId: Number(selectedKey),
        },
      });

      if (res?.data?.success) {
        sessionStorage.setItem(
          "grnConfirmSelectionPayload",
          JSON.stringify({
            grnInsId: Number(selectedKey),
            grn_type: rad === "HO" ? "HO" : "BRANCH",
            grnconf_purchaseorder: res.data.rows || [],
          })
        );

        toast.success("Saved selection");
        window.location.href = `/Inventory/goodsReceiptNoteConfirm_GstEntry`;
      } else {
        toast.error(res?.data?.message || "Save failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save error");
    } finally {
      setLoading(false);
    }
  };

  const handleProjectCodeChange = (val) => {
    setProjectCodeId(val);
    setProjectId(val);
  };

  const handleProjectNameChange = (val) => {
    setProjectId(val);
    setProjectCodeId(val);
  };

  // ---------------- lifecycle ----------------
  useEffect(() => {
    fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purType]);

  useEffect(() => {
    // clear list when mode changes
    setRows([]);
    setSelectedKey(null);
    setPage(1);
    setTotalCount(0);
    setTotalPages(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
    if (page > 1) fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // -------- table headers depend on mode --------
  const tableHeaders =
    mode === "A"
      ? ["Slno", "Purchase Order No.", "Date", "Supplier", "Due Date", "Status", "Check"]
      : ["Slno", "GRN Inspection No.", "GRN No.", "Date", "Supplier", "Status", "Check"];

  return (
    <LayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">GRN CONFIRM ADD LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {mode === "A" ? "Purchase Orders for GRN Confirmation" : "GRN(s) for Confirmation"}
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Supplier / Date / Project / Store + Pagination + Select & Save
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {totalCount} Records
              </div>

              <div className={`px-4 py-2 rounded-2xl text-xs font-bold ${isDark ? "bg-white/10 text-gray-200" : "bg-gray-100 text-gray-700"}`}>
                Mode: {mode} (grnre={String(flags.grnre)} / grninsre={String(flags.grninsre)})
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={handleRefresh} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Reset
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button onClick={handleSave} disabled={loading || !selectedKey} className={saveBtn}>
                <Save className="w-4 h-4" />
                Save
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelBase}>Branch</label>
                  <div className="mt-2 flex items-center gap-6">
                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="branch"
                        checked={rad === "HO"}
                        onChange={() => setRad("HO")}
                        disabled={branchType === "HO"}
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-700"}>H.O.</span>
                    </label>

                    <label className="inline-flex items-center gap-2">
                      <input
                        type="radio"
                        name="branch"
                        checked={rad === "BRANCH"}
                        onChange={() => setRad("BRANCH")}
                      />
                      <span className={isDark ? "text-gray-200" : "text-gray-700"}>BRANCH</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={labelBase}>Supplier</label>
                  <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {suppliers.map((x) => (
                      <option key={x.supplierId} value={x.supplierId}>
                        {x.supplierName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>From Date</label>
                  <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className={inputBase} />
                </div>

                <div>
                  <label className={labelBase}>To Date</label>
                  <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className={inputBase} />
                </div>

                {mode === "B" ? (
                  <div>
                    <label className={labelBase}>GRN No</label>
                    <input value={grnNo} onChange={(e) => setGrnNo(e.target.value)} placeholder="Type GRN No..." className={inputBase} />
                  </div>
                ) : (
                  <div>
                    <label className={labelBase}>Purchase Order No</label>
                    <input value={poNo} onChange={(e) => setPoNo(e.target.value)} placeholder="Type PO No..." className={inputBase} />
                  </div>
                )}

                <div>
                  <label className={labelBase}>Store</label>
                  <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {stores.map((st) => (
                      <option key={st.storeId} value={st.storeId}>
                        {st.storeName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Project Code</label>
                  <select value={projectCodeId} onChange={(e) => handleProjectCodeChange(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {projectsCode.map((p) => (
                      <option key={p.projectId} value={p.projectId}>
                        {p.projectCode}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>Project Name</label>
                  <select value={projectId} onChange={(e) => handleProjectNameChange(e.target.value)} className={inputBase}>
                    <option value="Select">Select</option>
                    {projectsName.map((p) => (
                      <option key={p.projectId} value={p.projectId}>
                        {p.projectName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    {tableHeaders.map((h) => (
                      <th
                        key={h}
                        className={`px-4 py-4 ${h === "Slno" || h === "Check" ? "text-center" : "text-left"} font-bold`}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: tableHeaders.length }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={tableHeaders.length} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => {
                      const key = mode === "A" ? Number(r.fld_purchaseorderid) : Number(r.fld_grninsid);

                      return (
                        <tr key={key} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(page - 1) * pageSize + idx + 1}
                          </td>

                          {mode === "A" ? (
                            <>
                              <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_pono}</td>
                              <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_date}</td>
                              <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_suppliername}</td>
                              <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_duedate}</td>
                              <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.status}</td>
                            </>
                          ) : (
                            <>
                              <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_grninsno}</td>
                              <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_grnno}</td>
                              <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_date}</td>
                              <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.fld_suppliername}</td>
                              <td className={`px-4 py-4 text-sm text-left ${isDark ? "text-gray-300" : "text-gray-900"}`}>{r.status}</td>
                            </>
                          )}

                          <td className="px-4 py-4 text-center">
                            <input
                              type="checkbox"
                              checked={selectedKey === key}
                              onChange={() => toggleSelectSingle(key)}
                              className="w-4 h-4"
                            />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{totalCount}</b>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {totalPages > 1 && (
                <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === 1 ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Prev
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setPage(i + 1)}
                      className={`px-4 py-2 text-sm font-bold transition ${
                        page === i + 1 ? `${primaryBtnClass} text-white` : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className={`px-4 py-2 text-sm font-bold ${
                      page === totalPages ? "text-gray-400 cursor-not-allowed" : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    Next
                  </button>
                </div>
              )}

              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh List
              </button>
            </div>
          </div>
        </div>
      </div>
    </LayoutWrapper>
  );
};

export default GoodsReceiptNoteConfirmAddListing;